export const applyFilter = (
  row,
  field,
  operator,
  value,
  fieldType,
  getCellValue
) => {
  // For numeric types (acv, count, number, currency, percentage), use raw value from row
  // to avoid parsing formatted strings. For other types, use getCellValue if available.
  let cellValue;
  if (fieldType === "acv" || fieldType === "count" || 
      fieldType === "number" || fieldType === "currency" || fieldType === "percentage") {
    // Use raw numeric value directly from row
    cellValue = row[field];
  } else {
    // For text/date types, use getCellValue which may format the value
    cellValue = getCellValue ? getCellValue(row, field) : row[field];
  }
  
  // Handle null/undefined/dash values
  if (cellValue == null || cellValue === "-") {
    if (operator === "equals" && value === "") return true;
    if (operator === "notEquals" && value !== "") return true;
    return false;
  }

  // Date filtering
  if (fieldType === "date") {
    const cellDate = cellValue ? new Date(cellValue) : null;
    if (!cellDate || isNaN(cellDate.getTime())) {
      // For notEquals, null/undefined values should match
      if (operator === "notEquals" && value) {
        return true;
      }
      return false;
    }

    // Reset time to start of day for proper comparison
    const cellDateStart = new Date(cellDate);
    cellDateStart.setHours(0, 0, 0, 0);
    const cellDateEnd = new Date(cellDate);
    cellDateEnd.setHours(23, 59, 59, 999);

    switch (operator) {
      case "equals": {
        const filterDate = value ? new Date(value) : null;
        if (!filterDate || isNaN(filterDate.getTime())) {
          return false;
        }
        const filterDateStart = new Date(filterDate);
        filterDateStart.setHours(0, 0, 0, 0);
        const filterDateEnd = new Date(filterDate);
        filterDateEnd.setHours(23, 59, 59, 999);
        // Check if cell date is within the same day as filter date
        return cellDateStart.getTime() === filterDateStart.getTime();
      }
      case "notEquals": {
        const filterDate = value ? new Date(value) : null;
        if (!filterDate || isNaN(filterDate.getTime())) {
          return true; // If filter date is invalid, all dates are not equal
        }
        const filterDateStart = new Date(filterDate);
        filterDateStart.setHours(0, 0, 0, 0);
        return cellDateStart.getTime() !== filterDateStart.getTime();
      }
      case "lessThan": {
        const filterDate = value ? new Date(value) : null;
        if (!filterDate || isNaN(filterDate.getTime())) {
          return false;
        }
        const filterDateStart = new Date(filterDate);
        filterDateStart.setHours(0, 0, 0, 0);
        return cellDateStart < filterDateStart;
      }
      case "lessThanOrEqual": {
        const filterDate = value ? new Date(value) : null;
        if (!filterDate || isNaN(filterDate.getTime())) {
          return false;
        }
        const filterDateStart = new Date(filterDate);
        filterDateStart.setHours(0, 0, 0, 0);
        return cellDateStart <= filterDateStart;
      }
      case "greaterThan": {
        const filterDate = value ? new Date(value) : null;
        if (!filterDate || isNaN(filterDate.getTime())) {
          return false;
        }
        const filterDateStart = new Date(filterDate);
        filterDateStart.setHours(0, 0, 0, 0);
        return cellDateStart > filterDateStart;
      }
      case "greaterThanOrEqual": {
        const filterDate = value ? new Date(value) : null;
        if (!filterDate || isNaN(filterDate.getTime())) {
          return false;
        }
        const filterDateStart = new Date(filterDate);
        filterDateStart.setHours(0, 0, 0, 0);
        return cellDateStart >= filterDateStart;
      }
      case "inRange": {
        // Parse range value: "startDate|endDate"
        const rangeParts = value ? value.split("|") : [];
        if (rangeParts.length !== 2) {
          return false;
        }
        const startDate = rangeParts[0] ? new Date(rangeParts[0]) : null;
        const endDate = rangeParts[1] ? new Date(rangeParts[1]) : null;
        if (!startDate || isNaN(startDate.getTime()) || !endDate || isNaN(endDate.getTime())) {
          return false;
        }
        const startDateStart = new Date(startDate);
        startDateStart.setHours(0, 0, 0, 0);
        const endDateEnd = new Date(endDate);
        endDateEnd.setHours(23, 59, 59, 999);
        // Check if cell date is within the range (inclusive)
        return cellDateStart >= startDateStart && cellDateEnd <= endDateEnd;
      }
      case "startsWith":
        // Date starts with: cell date is on or after filter date (backward compatibility)
        const filterDateStarts = value ? new Date(value) : null;
        if (!filterDateStarts || isNaN(filterDateStarts.getTime())) {
          return false;
        }
        const filterDateStartStarts = new Date(filterDateStarts);
        filterDateStartStarts.setHours(0, 0, 0, 0);
        return cellDateStart >= filterDateStartStarts;
      case "endsWith":
        // Date ends with: cell date is on or before filter date (backward compatibility)
        const filterDateEnds = value ? new Date(value) : null;
        if (!filterDateEnds || isNaN(filterDateEnds.getTime())) {
          return false;
        }
        const filterDateEndEnds = new Date(filterDateEnds);
        filterDateEndEnds.setHours(23, 59, 59, 999);
        return cellDateEnd <= filterDateEndEnds;
      default:
        return true;
    }
  }

  // Helper function to normalize percentage filter values
  // Converts "75", "75%", or "0.75" all to 0.75 (decimal format)
  const normalizePercentageValue = (val) => {
    if (val === "" || val == null) return 0;
    // Remove % sign if present
    const cleaned = String(val).replace(/%/g, "").trim();
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed)) return 0;
    // If value is >= 1, assume it's a percentage (e.g., 75) and convert to decimal (0.75)
    // If value is < 1, assume it's already a decimal (e.g., 0.75) and use as is
    return parsed >= 1 ? parsed / 100 : parsed;
  };

  // Number filtering (number, currency, percentage, and original data types like acv, count)
  if (fieldType === "number" || fieldType === "currency" || fieldType === "percentage" || 
      fieldType === "acv" || fieldType === "count") {
    const numValue = typeof cellValue === "number" ? cellValue : parseFloat(String(cellValue)) || 0;

    // For percentage fields, normalize the filter value
    const parseFilterValue = (val) => {
      if (fieldType === "percentage") {
        return normalizePercentageValue(val);
      }
      return parseFloat(val) || 0;
    };

    switch (operator) {
      case "equals":
        const filterNum = parseFilterValue(value);
        return Math.abs(numValue - filterNum) < 0.01; // Allow small floating point differences
      case "notEquals":
        const filterNumNot = parseFilterValue(value);
        return Math.abs(numValue - filterNumNot) >= 0.01;
      case "lessThan":
        const filterNumLess = parseFilterValue(value);
        return numValue < filterNumLess;
      case "lessThanOrEqual":
        const filterNumLessEqual = parseFilterValue(value);
        return numValue <= filterNumLessEqual;
      case "greaterThan":
        const filterNumGreater = parseFilterValue(value);
        return numValue > filterNumGreater;
      case "greaterThanOrEqual":
        const filterNumGreaterEqual = parseFilterValue(value);
        return numValue >= filterNumGreaterEqual;
      case "inRange":
        // Handle range format: "min-max" or "min, max"
        const rangeMatch = value.match(/(\d+(?:\.\d+)?%?)\s*[-,\s]\s*(\d+(?:\.\d+)?%?)/);
        if (rangeMatch) {
          const min = fieldType === "percentage" 
            ? normalizePercentageValue(rangeMatch[1]) 
            : parseFloat(rangeMatch[1]) || 0;
          const max = fieldType === "percentage" 
            ? normalizePercentageValue(rangeMatch[2]) 
            : parseFloat(rangeMatch[2]) || 0;
          return numValue >= min && numValue <= max;
        }
        return false;
      default:
        return true;
    }
  } else {
    // String/Text comparison
    const cellStr = String(cellValue).toLowerCase();
    const filterStr = value.toLowerCase();

    switch (operator) {
      case "contains":
        return cellStr.includes(filterStr);
      case "notContains":
        return !cellStr.includes(filterStr);
      case "startsWith":
        return cellStr.startsWith(filterStr);
      case "endsWith":
        return cellStr.endsWith(filterStr);
      default:
        return true;
    }
  }
};

